/**
 * Plain words for why an agent did not finish cleanly (DEC-197, UI_SDD §R.13).
 *
 * Everything here is read from what the scan recorded (`failure`, `warnings`, the skip
 * reason). Nothing is guessed: an agent with no recorded failure that failed reads
 * "Failed" and shows the message it saved, and the pop-up says what was not recorded.
 * No em-dashes (DEC-069).
 */
import type { AgentState } from '../../stores/scanStore'
import type { FailureKind } from '../../types/api'
import { shortDuration } from '../../utils/runTime'
import { SETTINGS_PATH } from '../settings/settingsSections'

export const KIND_HEADLINE: Record<FailureKind, string> = {
  timeout: 'Timed out',
  crash: 'Crashed',
  ai_service: 'Could not reach the AI service',
  ai_unavailable: 'AI was not available',
  not_available: 'Not available in this version',
  stopped: 'Stopped',
  interrupted: 'Interrupted by a restart',
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`
}

/** "Timed out after 10m", "Crashed while reading files", "Finished, with 3 problems". */
export function agentHeadline(a: AgentState): string {
  const f = a.failure
  if (a.status === 'failed') {
    if (!f) return 'Failed'
    if (f.kind === 'timeout') return a.duration_seconds > 0 ? `Timed out after ${shortDuration(a.duration_seconds)}` : 'Timed out'
    if (f.kind === 'crash' && f.step) return `Crashed while ${f.step}`
    return KIND_HEADLINE[f.kind] ?? 'Failed'
  }
  if (a.status === 'done_with_problems' || a.status === 'warned') {
    const n = Math.max(problemsOf(a).length, 1)
    return `Finished, with ${plural(n, 'problem')}`
  }
  if (a.status === 'skipped') return 'Skipped'
  return 'Finished'
}

/** One sentence on what it means for the report. */
export function agentMeaning(a: AgentState, description: string): string {
  const what = description ? ` (${description.charAt(0).toLowerCase()}${description.slice(1)})` : ''
  switch (a.status) {
    case 'failed':
      return a.findings_count > 0
        ? `The report keeps the ${plural(a.findings_count, 'finding')} it had, but its checks${what} did not finish.`
        : `The report has no results from ${a.label}${what}. Those checks are missing, not clean.`
    case 'done_with_problems':
      return `Its findings are in the report. Where it hit a problem, part of its checks${what} may be missing.`
    case 'warned':
      return `Its findings are in the report. A tool it relies on was not available, so part of its checks did not run.`
    case 'skipped':
      return 'The Planner decided this check does not apply to this repository, so nothing is missing because of it.'
    default:
      return ''
  }
}

/** Every recorded problem, in order. */
export function problemsOf(a: AgentState): string[] {
  const out: string[] = []
  if (a.status === 'failed') {
    const m = a.failure?.message ?? a.error
    if (m) out.push(m)
  }
  if (a.status === 'skipped' && a.skip_reason) out.push(a.skip_reason)
  for (const w of a.warnings ?? []) if (w && !out.includes(w)) out.push(w)
  if (out.length === 0 && a.warning) out.push(a.warning)
  return out
}

export interface ProblemGroup {
  /** The first message of the group, as written. */
  text: string
  items: string[]
}

/** The message with what varies between repeats (paths, file names, numbers) blanked. */
function shapeOf(msg: string): string {
  return msg
    .replace(/(["'`]).*?\1/g, '_')
    .replace(/\S*[/\\]\S*/g, '_')
    .replace(/\b[\w-]+\.[a-z0-9]{1,8}\b/gi, '_')
    .replace(/\d+/g, '_')
    .trim()
}

/** Repeated problems grouped, so "Could not read 4 files" expands to the list. */
export function groupProblems(problems: string[]): ProblemGroup[] {
  const groups = new Map<string, ProblemGroup>()
  for (const p of problems) {
    const key = shapeOf(p)
    const g = groups.get(key)
    if (g) g.items.push(p)
    else groups.set(key, { text: p, items: [p] })
  }
  return [...groups.values()]
}

export interface Remedy {
  text: string
  /** "rerun" or a path to open. */
  action?: 'rerun' | { to: string; label: string }
}

/** What to do, when there is something to do. Null otherwise: never filler. */
export function agentRemedy(a: AgentState): Remedy | null {
  if (a.status !== 'failed') return null
  switch (a.failure?.kind) {
    case 'timeout':
      return { text: 'Run the scan again. A large repository can take longer than the time limit.', action: 'rerun' }
    case 'ai_service':
      return { text: 'The AI service did not answer twice in a row. Running again usually works.', action: 'rerun' }
    case 'ai_unavailable':
      return {
        text: 'The AI key was missing, rejected or out of credit. An admin can add or replace it in Settings.',
        action: { to: SETTINGS_PATH.general, label: 'Open Settings' },
      }
    case 'crash':
      return { text: 'Run again. If it fails the same way, share the technical details with whoever runs TDIA.', action: 'rerun' }
    case 'interrupted':
      return { text: 'The server restarted while this ran. Run the scan again.', action: 'rerun' }
    default:
      return null
  }
}

/** The trace as lines, whatever shape the server sent. */
export function traceLines(trace: string | string[] | null | undefined): string[] {
  if (!trace) return []
  return Array.isArray(trace) ? trace : trace.split('\n')
}
